import React, { useState, useEffect } from 'react';
import {
    Save, Building, Palette, Globe, Mail, Phone, Upload,
    CheckCircle, AlertCircle, RefreshCw, FileText
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import LandingPageConfig from './LandingPageConfig';

const Settings = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('general');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        website: '',
        address: '',
        primaryColor: '#8B5CF6',
        secondaryColor: '#10B981',
        accentColor: '#F59E0B',
        logoUrl: ''
    });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

    useEffect(() => {
        fetchInstitutionDetails();
    }, [user]);

    useEffect(() => {
        return () => {
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const fetchInstitutionDetails = async () => {
        try {
            setLoading(true);
            const institutionId = user?.institutionId || user?.campus?.institutionId;

            if (institutionId) {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_BASE_URL}/institutions/${institutionId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setFormData({
                    name: response.data.name || '',
                    email: response.data.email || '',
                    phone: response.data.phone || '',
                    website: response.data.website || '',
                    address: response.data.address || '',
                    primaryColor: response.data.primaryColor || '#8B5CF6',
                    secondaryColor: response.data.secondaryColor || '#10B981',
                    accentColor: response.data.accentColor || '#F59E0B',
                    logoUrl: response.data.logo || ''
                });

                if (response.data.logo) {
                    setPreviewUrl(response.data.logo.startsWith('http') ? response.data.logo : `${API_BASE_URL.replace('/api', '')}${response.data.logo}`);
                }
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setMessage({ type: 'error', text: 'Failed to load institution details.' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);

        try {
            const token = localStorage.getItem('token');
            const institutionId = user?.institutionId || user?.campus?.institutionId;

            const data = new FormData();
            Object.keys(formData).forEach(key => {
                data.append(key, formData[key]);
            });

            if (logoFile) {
                data.append('logo', logoFile);
            }

            await axios.put(`${API_BASE_URL}/institutions/${institutionId}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setMessage({ type: 'success', text: 'Settings updated successfully!' });
        } catch (error) {
            console.error('Error updating settings:', error);
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update settings.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Loading settings...</div>;

    return (
        <div className="w-full space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Institution Settings</h1>
                <p className="text-gray-500">Manage your institution's profile, branding, and contact information.</p>
            </div>

            {message && (
                <div className={`p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                    {message.text}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="flex border-b border-gray-200 bg-gray-50">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={`px-6 py-4 text-sm font-medium transition flex items-center gap-2 ${activeTab === 'general'
                            ? 'bg-white border-t-2 border-indigo-600 text-indigo-600'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <Building className="h-4 w-4" />
                        General Information
                    </button>
                    <button
                        onClick={() => setActiveTab('branding')}
                        className={`px-6 py-4 text-sm font-medium transition flex items-center gap-2 ${activeTab === 'branding'
                            ? 'bg-white border-t-2 border-indigo-600 text-indigo-600'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <Palette className="h-4 w-4" />
                        Branding & Appearance
                    </button>
                    <button
                        onClick={() => setActiveTab('landing')}
                        className={`px-6 py-4 text-sm font-medium transition flex items-center gap-2 ${activeTab === 'landing'
                            ? 'bg-white border-t-2 border-indigo-600 text-indigo-600'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                            }`}
                    >
                        <FileText className="h-4 w-4" />
                        Landing Page
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {activeTab === 'general' && (
                        <div className="space-y-6 animate-fadeIn">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Institution Name</label>
                                    <div className="relative">
                                        <Building className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                        <input
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="e.g. Nairobi Pentecostal Bible College"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Website URL</label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                        <input
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="https://npbc.ac.ke"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                        <input
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="admin@npbc.ac.ke"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Contact Phone</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                                        <input
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="+254 700 000 000"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Physical Address</label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        rows="3"
                                        placeholder="Enter physical address..."
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'branding' && (
                        <div className="space-y-8 animate-fadeIn">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900 mb-4">Institution Logo</h3>
                                <div className="flex items-start gap-6">
                                    <div className="space-y-3">
                                        <div className="relative group w-32 h-32">
                                            <div className="w-full h-full bg-white rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300 overflow-hidden relative shadow-sm hover:border-indigo-500 transition-colors cursor-pointer">
                                                {previewUrl ? (
                                                    <img src={previewUrl} alt="Logo Preview" className="h-full w-full object-contain p-2" />
                                                ) : (
                                                    <div className="text-center p-2">
                                                        <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                                                        <span className="text-xs text-gray-500 mt-1 block">Upload Logo</span>
                                                    </div>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    title="Upload new logo"
                                                />
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => document.querySelector('input[type="file"]').click()}
                                            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                                        >
                                            Click to Upload
                                        </button>
                                    </div>

                                    <div className="flex-1 space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL (Optional)</label>
                                            <input
                                                name="logoUrl"
                                                value={formData.logoUrl}
                                                onChange={handleChange}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                placeholder="https://example.com/logo.png"
                                            />
                                            <p className="mt-2 text-sm text-gray-500">
                                                Upload an image directly or paste a URL. Recommended size: 500x500px, PNG format with transparent background.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-gray-200 pt-6">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <Palette className="h-5 w-5 text-gray-500" />
                                    Brand Colors
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                                        <div className="flex gap-2 items-center">
                                            <div className="relative overflow-hidden w-10 h-10 rounded-lg border border-gray-300 shadow-sm">
                                                <input
                                                    type="color"
                                                    name="primaryColor"
                                                    value={formData.primaryColor}
                                                    onChange={handleChange}
                                                    className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer border-none p-0"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                name="primaryColor"
                                                value={formData.primaryColor}
                                                onChange={handleChange}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg uppercase font-mono text-sm"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Main headers & buttons.</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                                        <div className="flex gap-2 items-center">
                                            <div className="relative overflow-hidden w-10 h-10 rounded-lg border border-gray-300 shadow-sm">
                                                <input
                                                    type="color"
                                                    name="secondaryColor"
                                                    value={formData.secondaryColor}
                                                    onChange={handleChange}
                                                    className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer border-none p-0"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                name="secondaryColor"
                                                value={formData.secondaryColor}
                                                onChange={handleChange}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg uppercase font-mono text-sm"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Accents & highlights.</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Accent Color</label>
                                        <div className="flex gap-2 items-center">
                                            <div className="relative overflow-hidden w-10 h-10 rounded-lg border border-gray-300 shadow-sm">
                                                <input
                                                    type="color"
                                                    name="accentColor"
                                                    value={formData.accentColor}
                                                    onChange={handleChange}
                                                    className="absolute -top-2 -left-2 w-16 h-16 cursor-pointer border-none p-0"
                                                />
                                            </div>
                                            <input
                                                type="text"
                                                name="accentColor"
                                                value={formData.accentColor}
                                                onChange={handleChange}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg uppercase font-mono text-sm"
                                            />
                                        </div>
                                        <p className="mt-1 text-xs text-gray-500">Special actions.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'landing' && (
                        <div className="space-y-6 animate-fadeIn">
                            <LandingPageConfig />
                        </div>
                    )}

                    {activeTab !== 'landing' && (
                        <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={fetchInstitutionDetails}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                                disabled={saving}
                            >
                                Reset Changes
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 font-medium shadow-sm"
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Settings;
